import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/CustomerSubscriptionsService';
import { CustomerSubscription } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useCustomerSubscription = (customerSubscriptionId?: string) => {
    const queryClient = useQueryClient();

    const customerSubscriptionQuery = useQuery({
        queryKey: ['customer-subscription', customerSubscriptionId],
        queryFn: () => fetch(customerSubscriptionId!),
        enabled: !!customerSubscriptionId
    });

    const createMutation = useMutation({
        mutationFn: (values: CustomerSubscription) => {
            const { created: _created, createdByUsername: _createdByUsername, createdByUser: _createdByUser, updated: _updated, updatedByUsername: _updatedByUsername, ...cleanData } = values;
            return add(cleanData);
        },
        onSuccess: () => {
            notifySuccess('Customer Subscription added');
            queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
        },
        onError: notifyError
    });

    const updateMutation = useMutation({
        mutationFn: (values: CustomerSubscription) => {
            if (!customerSubscriptionId) throw new Error('customerSubscriptionId is required parameter');
            return update(customerSubscriptionId, values);
        },
        onSuccess: () => {
            notifySuccess('Customer Subscription updated');
            queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['customer-subscription', customerSubscriptionId] });
        },
        onError: notifyError
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!customerSubscriptionId) throw new Error('customerSubscriptionId is required parameter');
            return deleteRecord(customerSubscriptionId);
        },
        onSuccess: () => {
            notifySuccess('Customer Subscription deleted');
            queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
        },
        onError: notifyError
    });

    return {
        record: customerSubscriptionQuery.data,
        loading: customerSubscriptionQuery.isLoading,
        fetchRecord: customerSubscriptionQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
